import React, { useRef, useState } from 'react';
import { Card, CardContent, Box, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import pptxgen from 'pptxgenjs';
import captureScreenshot from 'utils/screenshotUtils';

const JOURNEY_PHASES = ['discovery', 'evaluation', 'onboarding', 'upgrade', 'daily_use', 'support'];

const getPhaseLabel = (phase) => {
  return phase.charAt(0).toUpperCase() + phase.slice(1).replace('_', ' ');
};

const getNodeColor = (score) => {
  if (score > 0.5) return '#66bb6a'; // green
  if (score > 0) return '#64b5f6'; // blue
  if (score > -0.5) return '#ffa726'; // orange
  return '#ef5350'; // red
};

const getGradientBackground = (score) => {
  if (score > 0.5) {
    return 'linear-gradient(to bottom, rgba(102, 187, 106, 0.1), rgba(102, 187, 106, 0.01))';
  }
  if (score > 0) {
    return 'linear-gradient(to bottom, rgba(100, 181, 246, 0.1), rgba(100, 181, 246, 0.05))';
  }
  return 'linear-gradient(to bottom, rgba(239, 83, 80, 0.05),  rgba(239, 83, 80, 0.1))';
};

const adaptJourneyData = (data) => {
  if (!data) return null;
  // If data has a 'phase' property and it contains our expected phases, use that
  if (data.phase && JOURNEY_PHASES.some((phase) => data.phase[phase])) {
    return data.phase;
  }
  // If data directly contains our expected phases, use it as is
  if (JOURNEY_PHASES.some((phase) => data[phase])) {
    return data;
  }
  return null;
};

const UserJourney = () => {
  const user_journey_analysis = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.user_journey_analysis);
  const contentRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'user-journey-analysis',
      afterCapture: handleClose
    });
  };

  const convertToRTF = (data) => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    // Escape RTF characters
    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = JSON.stringify(text) || '';
      }
      return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
    };

    // Add title
    rtf += '\\b\\fs24 Customer Journey Analysis\\b0\\fs20\\par\\par\n';
    rtf += '\\i Sentiment across different stages of the user journey\\i0\\par\\par\n';

    // Add table
    rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
    rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\\cellx5000\n';
    rtf += '\\pard\\intbl\\b Stage\\b0\\cell\\b Sentiment Score\\b0\\cell\\b Emotion\\b0\\cell\\b Key Insights\\b0\\cell\\row\n';

    data.forEach((item) => {
      rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
      rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\\cellx5000\n';
      rtf += `\\pard\\intbl ${escapeRTF(getPhaseLabel(item.phase))}\\cell ${escapeRTF(item.sentiment_score.toFixed(1))}\\cell ${escapeRTF(
        item.emotion
      )}\\cell ${escapeRTF(item.quotes.join('\\par '))}\\cell\\row\n`;
    });

    rtf += '}';
    return rtf;
  };

  const downloadAsDoc = () => {
    if (!journeyData.length) return;

    const rtfContent = convertToRTF(journeyData);
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user-journey-analysis.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  const downloadAsPPTX = async () => {
    if (!journeyData.length) return;

    // Create a new presentation
    const pptx = new pptxgen();

    // Constants for table layout
    const ROWS_PER_SLIDE = 5; // Maximum number of rows per slide (including header)
    const totalSlides = Math.ceil(journeyData.length / ROWS_PER_SLIDE);

    // Create slides
    for (let slideIndex = 0; slideIndex < totalSlides; slideIndex++) {
      const slide = pptx.addSlide();

      // Set slide background
      slide.background = { color: 'FFFFFF' };

      // Add title
      slide.addText('Customer Journey Analysis', {
        x: 0.5,
        y: 0.5,
        w: '90%',
        h: 1,
        fontSize: 24,
        color: '363636',
        align: 'center',
        bold: true
      });

      // Add subtitle
      const subtitle =
        totalSlides > 1
          ? `Sentiment across different stages of the user journey (${slideIndex + 1}/${totalSlides})`
          : 'Sentiment across different stages of the user journey';

      slide.addText(subtitle, {
        x: 0.5,
        y: 1.2,
        w: '90%',
        h: 0.5,
        fontSize: 14,
        color: '666666',
        align: 'center',
        italic: true
      });

      // Calculate data range for this slide
      const startIndex = slideIndex * (ROWS_PER_SLIDE - 1); // -1 to account for header row
      const endIndex = Math.min(startIndex + (ROWS_PER_SLIDE - 1), journeyData.length);
      const slideData = journeyData.slice(startIndex, endIndex);

      // Create table data
      const tableData = [
        ['Stage', 'Sentiment Score', 'Emotion', 'Key Insights'],
        ...slideData.map((item) => [getPhaseLabel(item.phase), item.sentiment_score.toFixed(1), item.emotion, item.quotes.join('\n')])
      ];

      // Add table
      slide.addTable(tableData, {
        x: 0.25,
        y: 2,
        w: '95%',
        h: '70%',
        colW: [2, 1.2, 1.5, 4],
        rowH: 0.4,
        margin: 0,
        border: { type: 'solid', color: 'CCCCCC', pt: 1 },
        fontSize: 11,
        color: '363636',
        align: 'left',
        valign: 'middle',
        headerRows: 1,
        headerColor: 'F5F5F5',
        headerFontSize: 11,
        headerAlign: 'center',
        headerBold: true,
        autoPage: false,
        autoPageRepeatHeader: false,
        lineSize: 0.5
      });

      // Add "Powered by Askpot" branding as a horizontal row at the top right
      // 1. Powered by
      slide.addText('Powered by', {
        x: 7.0,
        y: 0.5,
        w: 1.1,
        h: 0.4,
        fontSize: 10,
        color: '666666',
        align: 'right',
        isTextBox: true
      });
      // 2. Logo (favicon.svg)
      slide.addImage({
        path: '/favicon.svg',
        x: 8.15,
        y: 0.51,
        w: 0.3,
        h: 0.3
      });
      // 3. Askpot text
      slide.addText('Askpot', {
        x: 8.5,
        y: 0.5,
        w: 1,
        h: 0.4,
        fontSize: 13,
        color: '363636',
        bold: true,
        align: 'left',
        isTextBox: true
      });
    }

    // Save the presentation
    pptx.writeFile({ fileName: 'user-journey-analysis.pptx' });
    handleClose();
  };

  const adaptedData = adaptJourneyData(user_journey_analysis);
  if (!adaptedData) return null;

  const journeyData = JOURNEY_PHASES.map((phase) => {
    const phaseData = adaptedData[phase];
    return {
      phase,
      sentiment_score: phaseData.sentiment ?? 0,
      emotion: phaseData.business_priority,
      quotes: phaseData.key_insights?.length ? phaseData.key_insights : ['Not mentioned in reviews']
    };
  });

  return (
    <Card sx={{ width: '100%', mt: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: '16px' }}>
      <CardContent ref={contentRef}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            Customer Journey Map
          </Typography>
          <Box className="screenshot-controls">
            <IconButton onClick={handleClick} size="small" sx={{ height: 28 }} aria-label="download options">
              <DownloadIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }}
            >
              <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
              <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
              <MenuItem onClick={downloadAsPPTX}>Download as PPTX</MenuItem>
            </Menu>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Sentiment across different stages of the user journey
        </Typography>

        <Box sx={{ position: 'relative', width: '100%', height: '600px' }}>
          {/* Y-axis labels */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '50px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              fontSize: '0.75rem',
              color: 'text.secondary',
              py: 5
            }}
          >
            <Typography variant="caption">+1.0</Typography>
            <Typography variant="caption">+0.5</Typography>
            <Typography variant="caption">0</Typography>
            <Typography variant="caption">-0.5</Typography>
            <Typography variant="caption">-1.0</Typography>
          </Box>

          {/* Stage columns */}
          <Box
            sx={{
              position: 'absolute',
              left: '50px',
              right: 0,
              top: 0,
              bottom: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${journeyData.length}, 1fr)`
            }}
          >
            {journeyData.map((stage, index) => (
              <Box
                key={index}
                sx={{
                  height: '100%',
                  background: getGradientBackground(stage.sentiment_score)
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    typography: 'subtitle2'
                  }}
                >
                  <Typography variant="subtitle2">{getPhaseLabel(stage.phase)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stage.emotion}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Horizontal grid lines */}
          <Box
            sx={{
              position: 'absolute',
              left: '50px',
              right: 0,
              top: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              py: 5,
              pointerEvents: 'none'
            }}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ borderColor: 'divider', width: '100%' }} />
            ))}
          </Box>

          {/* Journey nodes and quotes */}
          <Box
            sx={{
              position: 'absolute',
              left: '50px',
              right: 0,
              top: '40px',
              bottom: '40px'
            }}
          >
            {/* SVG for connecting lines */}
            <svg
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                top: 0,
                left: 0
              }}
            >
              {journeyData.map((item, index) => {
                if (index < journeyData.length - 1) {
                  const x1 = ((index + 0.5) / journeyData.length) * 100;
                  const y1 = 50 - item.sentiment_score * 30;

                  const x2 = ((index + 1.5) / journeyData.length) * 100;
                  const y2 = 50 - journeyData[index + 1].sentiment_score * 30;

                  return (
                    <line key={`line-${index}`} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`} stroke="#d1d5db" strokeWidth="2" />
                  );
                }
                return null;
              })}
            </svg>

            {/* Nodes and quotes */}
            {journeyData.map((stage, index) => {
              const xPos = ((index + 0.5) / journeyData.length) * 100;
              const yPos = 50 - stage.sentiment_score * 30;

              return (
                <Box
                  key={`node-${index}`}
                  sx={{
                    position: 'absolute',
                    left: `${xPos}%`,
                    top: `${yPos}%`
                  }}
                >
                  {/* Node */}
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: getNodeColor(stage.sentiment_score),
                      position: 'absolute',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10
                    }}
                  />

                  {/* Score */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bgcolor: 'background.paper',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '9999px',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 1,
                      zIndex: 20,
                      transform: 'translate(-50%, -50%)',
                      left: 0,
                      mb: 1,
                      top:
                        stage.sentiment_score >= 0.79
                          ? '25px'
                          : stage.sentiment_score <= -0.79
                          ? '-25px'
                          : stage.sentiment_score >= 0
                          ? '25px'
                          : '-25px'
                    }}
                  >
                    {stage.sentiment_score === 0 ? 'N/A' : stage.sentiment_score.toFixed(1)}
                  </Box>

                  {/* Quote bubble */}
                  <Box
                    sx={{
                      position: 'absolute',
                      zIndex: 20,
                      width: '170px',
                      left: '-90px',
                      ...(stage.sentiment_score >= 0.79
                        ? { top: '50px' }
                        : stage.sentiment_score <= -0.79
                        ? { bottom: '50px' }
                        : stage.sentiment_score >= 0
                        ? { top: '50px' }
                        : { bottom: '50px' })
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.5,
                        position: 'relative'
                      }}
                    >
                      {stage.quotes.map((quote, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{
                            fontStyle: 'italic',
                            mb: index < stage.quotes.length - 1 ? 1 : 0,
                            display: 'flex',
                            alignItems: 'flex-start'
                          }}
                        >
                          <Box component="span" sx={{ mr: 1, lineHeight: 1.5 }}>
                            •
                          </Box>
                          <Box component="span">{quote}</Box>
                        </Typography>
                      ))}
                      {/* Triangle pointer */}
                      <Box
                        sx={{
                          position: 'absolute',
                          width: '12px',
                          height: '12px',
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          transform: 'rotate(45deg)',
                          left: '50%',
                          ml: '-6px',
                          boxShadow: '0 0 1px rgba(0,0,0,0.1)',
                          ...(stage.sentiment_score >= 0.79
                            ? {
                                top: '-6px',
                                borderRight: 'none',
                                borderBottom: 'none'
                              }
                            : stage.sentiment_score <= -0.79
                            ? {
                                bottom: '-6px',
                                borderTop: 'none',
                                borderLeft: 'none'
                              }
                            : stage.sentiment_score >= 0
                            ? {
                                top: '-6px',
                                borderRight: 'none',
                                borderBottom: 'none'
                              }
                            : {
                                bottom: '-6px',
                                borderTop: 'none',
                                borderLeft: 'none'
                              })
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Experience labels */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50px',
              right: 0,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr'
            }}
          >
            <Box
              sx={{
                bgcolor: 'primary.lighter',
                color: 'primary.dark',
                py: 1,
                textAlign: 'center',
                borderTopLeftRadius: 1,
                borderBottomLeftRadius: 1,
                typography: 'subtitle2'
              }}
            >
              Pre-purchase experience
            </Box>
            <Box
              sx={{
                bgcolor: 'success.lighter',
                color: 'success.dark',
                py: 1,
                textAlign: 'center',
                borderTopRightRadius: 1,
                borderBottomRightRadius: 1,
                typography: 'subtitle2'
              }}
            >
              Post-purchase experience
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserJourney;
